export const ChromeTokens = {
  color: {
    base: '#f5e8c8',
    baseMuted: '#e9dcba',
    ink: '#2f3a45',
    inkMuted: '#6b7580',
    sky: '#c2d5db',
    sun: '#e8c887',
    grass: '#a8bf91',
    coral: '#d99878',
    lavender: '#b8a8c8',
    danger: '#b86a5a',
    rose: '#d8a8a8',
    sand: '#d4b896',
    clay: '#98664d',
  },
  // Derived tints — `color-mix()` recipes for admin-surface state cues.
  // Introduced with the Chip primitive (design_handoff_hero_sourcing/README
  // §Design-token additions). All six are mixes of an existing brand color,
  // never a new hex. Requires modern browsers (Chrome 111+, Safari 16.4+,
  // Firefox 113+) — we render as inline style strings so no PostCSS step
  // needed. `grass` = affirmative; `sun` = success; `coral` = warm error;
  // `neutralSurface` = neutral fill.
  stateTints: {
    grassSurface: 'color-mix(in srgb, #a8bf91 24%, white)',
    grassBorder: 'color-mix(in srgb, #a8bf91 72%, #2f3a45)',
    grassDeep: 'color-mix(in srgb, #a8bf91 48%, #2f3a45)',
    neutralSurface: 'color-mix(in srgb, #e9dcba 50%, white)',
    sunSurface: 'color-mix(in srgb, #e8c887 30%, white)',
    sunBorder: 'color-mix(in srgb, #e8c887 70%, #2f3a45)',
    coralSurface: 'color-mix(in srgb, #d99878 26%, white)',
    coralBorder: 'color-mix(in srgb, #d99878 60%, #2f3a45)',
    // `sky` as a ROLE: two things held together. The touch-parity handoff
    // picked it because it was the only palette token with no job, and one
    // colour across the stage tether, the cast-rail pair, the moment-card row
    // and the time-track span is what lets a child learn "blue means stuck
    // together" once and read the other three for free. No new hex.
    skySurface: 'color-mix(in srgb, #c2d5db 30%, white)',
    skyBorder: 'color-mix(in srgb, #c2d5db 68%, #2f3a45)',
    skyDeep: 'color-mix(in srgb, #c2d5db 46%, #2f3a45)',
  },
  radius: { sm: 8, md: 16, lg: 24, pill: 999 },
  shadow: {
    soft: '0 4px 12px rgba(47,58,69,0.10)',
    lifted: '0 10px 24px rgba(47,58,69,0.12)',
    pressed: 'inset 0 2px 4px rgba(47,58,69,0.15)',
  },
  spacing: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24 },
  topBarHeight: { desktop: 56, mobile: 52 },
  // Same file as tokens.css `--chrome-texture-paper-grain` — keep them in step.
  texture: { paperGrainUrl: '/chrome/paper-grain.webp' },
  font: {
    ui: "'Nunito', system-ui, sans-serif",
    display: "'Patrick Hand', cursive",
  },
  motion: {
    softEase: 'cubic-bezier(0.22, 0.9, 0.3, 1)',
    pop: '260ms cubic-bezier(0.22, 0.9, 0.3, 1)',
    glow: '2400ms ease-in-out infinite',
  },
} as const;

export type ChromeColor = keyof typeof ChromeTokens.color;
